import { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import PageList from '../../templates/PageList';
import FilterBar, { AppliedFilter, FilterSchema } from '../collateral/shared/FilterBar';
import { ATableHeader } from '../../organisms/ATable';
import StatusReasonModal from './StatusReasonModal';
import CompositionDeactivateModal from './composition/CompositionDeactivateModal';
import ChannelTabs from './composition/list/ChannelTabs';
import MessageCompositionRow from './composition/list/MessageCompositionRow';
import {
    MessageCompositionListFilters,
    useMessageCompositionList,
} from './composition/list/useMessageCompositionList';
import communicationApi from '../../../services/communicationApi';
import { ChannelType, MessageComposition } from '../../../types/communication';

const STATUS_OPTIONS = [
    { value: 'ACTIVE', label: 'Ativo' },
    { value: 'INACTIVE', label: 'Inativo' },
];

const TABLE_HEADERS: ATableHeader[] = [
    { id: 'c-name', title: 'Composição', field: 'name', canSort: true },
    { id: 'c-group', title: 'Grupo', field: 'groupId', canSort: false, width: '130px' },
    { id: 'c-content', title: 'Conteúdo', field: 'templateIds', canSort: false, width: '130px' },
    { id: 'c-trigger', title: 'Disparo', field: 'trigger', canSort: false, width: '190px' },
    { id: 'c-updated', title: 'Atualizado', field: 'updatedAt', canSort: false, width: '110px' },
    { id: 'c-status', title: 'Status', field: 'status', canSort: false, width: '90px' },
    { id: 'c-actions', title: '', field: '', canSort: false, width: '56px' },
];

const MessageCompositionPageList = () => {
    const navigate = useNavigate();
    const { page = '0', pageSize = '20', field = 'name', order = 'ASC' } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const pageNum = Number(page);
    const sizeNum = Number(pageSize);

    const filters: MessageCompositionListFilters = {
        search: searchParams.get('search') ?? '',
        channel: (searchParams.get('channel') as ChannelType | '') ?? '',
        groupId: searchParams.get('groupId') ?? '',
        status: (searchParams.get('status') as 'ACTIVE' | 'INACTIVE' | '') ?? '',
    };

    const list = useMessageCompositionList(
        { page: pageNum, pageSize: sizeNum, field, order: order === 'DESC' ? 'DESC' : 'ASC' },
        filters,
    );
    const [toggleTarget, setToggleTarget] = useState<MessageComposition | null>(null);
    const [searchInput, setSearchInput] = useState(filters.search);

    const filterSchema: FilterSchema[] = [
        {
            key: 'groupId',
            label: 'Grupo',
            type: 'enum',
            options: list.groups.map((g) => ({ value: g.id, label: g.name })),
        },
        { key: 'status', label: 'Status', type: 'enum', options: STATUS_OPTIONS },
    ];

    const initialFilters: AppliedFilter[] = [
        ...(filters.groupId ? [{ key: 'groupId', value: filters.groupId }] : []),
        ...(filters.status ? [{ key: 'status', value: filters.status }] : []),
    ];

    const urlNavigate = (p: number, s: number, f: string, o: string) =>
        `/communication/message-compositions/${p}/${s}/${f}/${o}?${searchParams.toString()}`;

    const applyFilters = (patch: Partial<MessageCompositionListFilters>) => {
        const next = { ...filters, ...patch };
        const params = new URLSearchParams();
        if (next.search) params.set('search', next.search);
        if (next.channel) params.set('channel', next.channel);
        if (next.groupId) params.set('groupId', next.groupId);
        if (next.status) params.set('status', next.status);
        setSearchParams(params, { replace: true });
        navigate(`/communication/message-compositions/0/${sizeNum}/${field}/${order}?${params.toString()}`, { replace: true });
    };

    const onFilterBarSearch = (applied: AppliedFilter[]) => {
        const byKey = Object.fromEntries(applied.map((a) => [a.key, a.value]));
        applyFilters({
            groupId: byKey.groupId ?? '',
            status: (byKey.status as 'ACTIVE' | 'INACTIVE' | '') ?? '',
        });
    };

    const handleAction = async (meta: MessageComposition, action: 'open' | 'edit' | 'duplicate' | 'toggle') => {
        if (action === 'open') navigate(`/communication/message-composition/${meta.id}`);
        if (action === 'edit') navigate(`/communication/message-composition/${meta.id}/edit`);
        if (action === 'toggle') setToggleTarget(meta);
        if (action === 'duplicate') {
            try {
                const clone = await communicationApi.duplicateMessageComposition(meta.id);
                toast.success('Composição duplicada.');
                navigate(`/communication/message-composition/${clone.id}/edit`);
            } catch {
                toast.error('Falha ao duplicar.');
            }
        }
    };

    const renderRows = () => {
        if (!list.rows.length) {
            return (
                <tr>
                    <td colSpan={TABLE_HEADERS.length} className="py-12 px-2 text-center">
                        <div className="text-sm font-semibold text-gray-600 dark:text-gray-300">Nenhuma composição encontrada</div>
                        <div className="text-xs text-gray-400 mt-1">Ajuste os filtros ou crie uma composição nova.</div>
                    </td>
                </tr>
            );
        }
        return list.rows.map((meta, index) => (
            <MessageCompositionRow
                key={meta.id}
                meta={meta}
                striped={index % 2 === 0}
                group={list.groups.find((g) => g.id === meta.groupId)}
                variableCount={list.variableCount(meta)}
                triggers={list.triggers(meta)}
                onOpen={() => navigate(`/communication/message-composition/${meta.id}`)}
                onAction={(action) => handleAction(meta, action)}
            />
        ));
    };

    return (
        <>
            <PageList
                pageTitle="Composições de mensagem"
                url=""
                breadcrumb={[
                    { label: 'Comunicação', url: '#' },
                    { label: 'Composições', url: '#' },
                ]}
                tableFieldSort={field}
                tableOrderSort={order as 'ASC' | 'DESC'}
                onTableSort={(f, o) => navigate(urlNavigate(pageNum, sizeNum, f, o), { replace: true })}
                renderRows={renderRows as any}
                page={pageNum}
                pageSize={sizeNum}
                onPageChange={(p) => navigate(urlNavigate(p, sizeNum, field, order), { replace: true })}
                onItemsPerPageChange={(s) => navigate(urlNavigate(0, s, field, order), { replace: true })}
                itemsLabel="composições"
                defaultBackendHeader={false}
                tableHeaders={TABLE_HEADERS}
                isLoadingData={list.loading}
                externalTotalElements={list.total}
                externalTotalPages={list.totalPages}
            >
                {{
                    controlSlot: (
                        <div className="flex justify-end m-2">
                            <button
                                className="text-white bg-blue-500 hover:bg-blue-700 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded text-sm px-5 py-2.5 text-center whitespace-nowrap"
                                onClick={() => navigate('/communication/message-composition/new')}
                            >
                                Nova composição
                            </button>
                        </div>
                    ),
                    filterSlot: (
                        <FilterBar
                            schema={filterSchema}
                            initialFilters={initialFilters}
                            onSearch={onFilterBarSearch}
                            searchValue={searchInput}
                            onSearchChange={setSearchInput}
                            onSearchSubmit={(value?: string) => applyFilters({ search: value ?? searchInput })}
                            searchPlaceholder="Procurar por nome ou descrição..."
                            searchInputClassName="w-72"
                            loading={list.loading}
                            leadingSlot={
                                <ChannelTabs
                                    value={filters.channel}
                                    counts={list.channelCounts}
                                    onChange={(channel) => applyFilters({ channel })}
                                />
                            }
                        />
                    ),
                }}
            </PageList>
            <CompositionDeactivateModal
                isOpen={Boolean(toggleTarget) && toggleTarget?.status === 'ACTIVE'}
                compositionLabel={toggleTarget?.name ?? 'composição'}
                onCancel={() => setToggleTarget(null)}
                onConfirm={async (reason) => {
                    if (toggleTarget) await list.toggleStatus(toggleTarget, reason);
                    setToggleTarget(null);
                }}
            />
            <StatusReasonModal
                isOpen={Boolean(toggleTarget) && toggleTarget?.status !== 'ACTIVE'}
                title="Ativar composição"
                actionLabel="Ativar"
                onCancel={() => setToggleTarget(null)}
                onConfirm={async (reason) => {
                    if (toggleTarget) await list.toggleStatus(toggleTarget, reason);
                    setToggleTarget(null);
                }}
            />
        </>
    );
};

export default MessageCompositionPageList;
